import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, ArrowRight, ExternalLink, ShieldCheck, GraduationCap,
  Mic, Phone, Users, Award, Eye, Wallet, Puzzle, Layers, Play, Youtube,
} from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { StickyCta } from "@/components/site/StickyCta";
import { Modal } from "@/components/site/Modal";
import { Section, Eyebrow, Heading } from "@/components/site/pageui";
import { JsonLd, personSchema, profilePageSchema, breadcrumbSchema, nniInterviewVideoSchema } from "@/components/site/Schema";
import { TestimonialCard } from "@/components/site/Testimonials";
import { getTestimonials, ABOUT_IDS, ABOUT_MORE } from "@/lib/testimonials";
import portrait from "@/assets/shawn-portrait.png";
import family from "@/assets/king-family-purpose.jpg";
import travelCastle from "@/assets/travel-castle.jpg";
import travelSkyline from "@/assets/travel-skyline.jpg";
import travelPavilion from "@/assets/travel-pavilion.jpg";
import credential from "@/assets/ibc-credential.webp";

const PRACTITIONER_URL = "https://infinitebanking.org/practitioners/king882/";
const NNI_VIDEO_WATCH = "https://www.youtube.com/watch?v=iVnoFZpVcqg";
const NNI_EPISODE_URL = "https://infinitebanking.org/podcasts/episode-57-shawn-king/";

const lessons = [
  { icon: Eye, t: "Understand Before You Invest", d: "High potential returns never remove the need for due diligence and real understanding." },
  { icon: Wallet, t: "Protect Liquidity and Control", d: "Access and flexibility matter as much as growth. Capital should stay usable." },
  { icon: Puzzle, t: "Define the Job Before Choosing the Tool", d: "A financial product only makes sense once the problem it must solve is clear." },
  { icon: Layers, t: "Build Systems Instead of Chasing Tactics", d: "Coordinated systems outlast disconnected wins and one-off opportunities." },
];

const principles = [
  { t: "Understand Before You Implement", d: "Know the process, tradeoffs, costs, and long-term expectations before making a decision." },
  { t: "Protect the Foundation", d: "Capital protection, liquidity, and stability come before pursuing higher-risk opportunities." },
  { t: "Build Systems, Not Random Products", d: "Every financial tool should have a clear role inside one coordinated strategy." },
  { t: "Create a Life Worth Living", d: "Planning should support family, time, purpose, experiences, and meaningful work." },
];

const roles = [
  { icon: ShieldCheck, t: "Founder of Legacy Life & Retirement" },
  { icon: Award, t: "Authorized Infinite Banking Practitioner" },
  { icon: GraduationCap, t: "Financial Educator" },
  { icon: Mic, t: "Speaker and Content Creator" },
];

const purposeValues = [
  "Time and presence with family first",
  "Travel and shared experiences as education",
  "Flexibility to create opportunity, not just accumulate",
];

const process = [
  { tag: "Step One", t: "First Conversation", d: "A 45-minute introductory phone call focused on your current situation, financial education, priorities, and initial questions." },
  { tag: "Step Two", t: "Deeper Conversation", d: "A second 45-minute phone conversation that explores goals, cash flow, possible applications, and appropriate next educational steps." },
  { tag: "Step Three", t: "Continued Education", d: "Where appropriate, Shawn helps clients understand how the concepts, tools, repayment behavior, and broader priorities work together over time." },
];

export default function About() {
  const [more, setMore] = useState(false);
  const primary = getTestimonials(ABOUT_IDS);
  const extra = getTestimonials(ABOUT_MORE);

  return (
    <div className="min-h-screen bg-ivory" data-testid="about-page">
      <SEO
        title="About Shawn King | Legacy Life & Retirement"
        description="Meet Shawn King, Authorized Infinite Banking practitioner, financial educator, and founder of Legacy Life & Retirement."
      />
      <JsonLd id="ld-about-profilepage" data={profilePageSchema()} />
      <JsonLd id="ld-person" data={personSchema()} />
      <JsonLd id="ld-about-breadcrumb" data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])} />
      <JsonLd id="ld-nni-video" data={nniInterviewVideoSchema()} />
      <Header />
      <main>
        {/* 1. Hero */}
        <section data-testid="hero-section" className="relative overflow-hidden bg-[#E9DFCF] pt-[120px] md:pt-[150px] pb-20 md:pb-24 border-b border-bronze/20">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-50 md:opacity-70 bg-[repeating-linear-gradient(0deg,rgba(120,100,70,0.05)_0px,rgba(120,100,70,0.05)_1px,transparent_1px,transparent_4px),repeating-linear-gradient(90deg,rgba(120,100,70,0.04)_0px,rgba(120,100,70,0.04)_1px,transparent_1px,transparent_4px)]" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 grid-lines opacity-[0.16]" />
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-[-4rem] h-[32rem] w-[32rem] rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.28),transparent_65%)]" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 shadow-[inset_0_0_150px_rgba(120,90,50,0.16)]" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
          <div className="container-lr relative z-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
            <div className="max-w-xl">
              <Eyebrow>Meet Shawn King</Eyebrow>
              <h1 className="mt-6 font-serif font-semibold tracking-display text-legacy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                Financial Education Built From <span className="accent-word">Real Experience.</span>
              </h1>
              <p className="mt-7 text-slate text-base md:text-lg leading-relaxed">
                Shawn King is an Authorized Infinite Banking practitioner, financial educator, and founder of Legacy Life &amp; Retirement.
              </p>
              <p className="mt-4 text-slate/90 text-sm md:text-base leading-relaxed">
                His work is focused on helping regular families and business owners better understand cash flow, financial control, retirement planning, and the systems that can help build a lasting family legacy.
              </p>
              <div className="mt-9">
                <Link to="/strategy-call" data-testid="about-hero-cta" className="btn-base btn-primary"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
              </div>
            </div>
            {/* Portrait on Heritage Gold panel */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-card bg-[linear-gradient(140deg,#C7A15A_0%,#dcbd82_45%,#a97f3d_100%)] p-4 md:p-5 shadow-[0_40px_80px_-40px_rgba(120,90,40,0.65)]">
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 grid-lines opacity-[0.12]" />
                <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-10 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.35),transparent_65%)]" />
                <img src={portrait} alt="Shawn King, founder of Legacy Life & Retirement" className="relative rounded-[0.9rem] w-full object-cover shadow-[0_20px_40px_-24px_rgba(17,17,17,0.55)]" />
              </div>
              <div aria-hidden="true" className="absolute -inset-2 rounded-card border border-gold/40 -z-10" />
            </div>
          </div>
        </section>

        {/* 2. Real Experience (combined story) */}
        <Section id="story" className="bg-ivory">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <Eyebrow>Real Experience</Eyebrow>
              <Heading>The Approach Was Built Through Experience, Mistakes, and <span className="accent-word">Better Questions.</span></Heading>
              <div className="mt-7 space-y-4 text-slate text-base leading-relaxed">
                <p>Shawn’s approach began with a simple realization: most families are not struggling because they lack discipline or ambition. They are struggling because no one ever showed them how cash flow, protection, liquidity, debt, investing, and long-term planning should work together.</p>
                <p>Years of building businesses, making investments, and navigating both successful and costly decisions gave him a clearer view of what matters most. Financial progress requires more than chasing returns. It requires understanding the risks, maintaining control, and giving every financial tool a defined purpose.</p>
                <p>That perspective became the foundation of Legacy Life &amp; Retirement—an education-first process designed to help families slow down, see the full picture, and build a financial system they can understand, manage, and strengthen over time.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {lessons.map(({ icon: Icon, t, d }) => (
                <div key={t} tabIndex={0} className="group rounded-card border border-taupe/50 bg-white p-6 outline-none transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-[0_18px_40px_-28px_rgba(17,17,17,0.5)] focus-visible:-translate-y-1 focus-visible:border-gold">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-legacy text-gold"><Icon size={18} strokeWidth={1.6} /></span>
                  <h3 className="mt-4 font-serif text-lg text-legacy leading-tight">{t}</h3>
                  <p className="mt-2 text-slate text-[0.88rem] leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* 3. The Purpose Behind the Plan */}
        <Section id="family" className="bg-[#EFE8D9]">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <div aria-hidden="true" className="absolute -top-4 -left-4 h-20 w-20 border-t border-l border-gold/70" />
              <div aria-hidden="true" className="absolute -bottom-4 -right-4 h-20 w-20 border-b border-r border-gold/70" />
              <div className="relative rounded-card overflow-hidden bg-white shadow-[0_30px_60px_-35px_rgba(17,17,17,0.5)]">
                <img src={family} alt="Shawn King with his wife Amanda and their son" loading="lazy" className="w-full aspect-[3/4] object-cover object-center" />
              </div>
            </div>
            <div>
              <Eyebrow>The Purpose Behind the Plan</Eyebrow>
              <Heading><span className="accent-word">Family</span> Is the Reason the Financial System Matters.</Heading>
              <p className="mt-7 text-slate text-base leading-relaxed">Shawn and his wife Amanda have built their family life around presence, shared experiences, and using travel as part of their son’s education — not simply accumulating more.</p>
              <p className="mt-4 text-slate text-base leading-relaxed">Their son is a central part of the motivation to build financial systems that create flexibility, opportunity, and generational momentum.</p>
              <div className="mt-7 space-y-3">
                {purposeValues.map((v) => (
                  <div key={v} className="flex items-center gap-3 rounded-xl border border-taupe/50 bg-white px-4 py-3">
                    <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-gold" />
                    <span className="text-legacy/90 text-[0.9rem] font-medium">{v}</span>
                  </div>
                ))}
              </div>
              <Link to="/family-legacy" data-testid="about-family-cta" className="btn-base btn-outline-dark mt-8">Explore Family &amp; Legacy<ArrowRight size={18} /></Link>
            </div>
          </div>
        </Section>

        {/* 3b. The Life Behind the Plan (travel / lifestyle) */}
        <Section id="life" className="bg-ivory">
          <div className="max-w-3xl">
            <Eyebrow>The Life Behind the Plan</Eyebrow>
            <Heading>Financial Freedom Should Create More Time for <span className="accent-word">What Matters.</span></Heading>
            <p className="mt-7 text-slate text-base leading-relaxed">Shawn and Amanda have built their financial decisions around having more control over their time, creating meaningful experiences with their family, and using travel as part of their son’s education. The goal of the financial system is not simply to accumulate more. It is to create the flexibility to live with greater intention.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 items-start">
            {[
              { src: travelSkyline, alt: "The King family in front of a city skyline at night", off: "lg:mt-10" },
              { src: travelCastle, alt: "Shawn, Amanda and their son at Osaka Castle in Japan" },
              { src: travelPavilion, alt: "Shawn and Amanda at a traditional Chinese pavilion", off: "lg:mt-10" },
            ].map((img) => (
              <div key={img.alt} className={`group relative overflow-hidden rounded-card border border-gold/30 shadow-[0_24px_50px_-32px_rgba(17,17,17,0.5)] ${img.off || ""}`}>
                <img src={img.src} alt={img.alt} loading="lazy" className={`aspect-[3/4] w-full ${img.fit || "object-cover"} object-center transition-transform duration-500 group-hover:scale-[1.04]`} />
              </div>
            ))}
          </div>
        </Section>

        {/* 4. Why Legacy Life & Retirement Exists (combined purpose + philosophy) */}
        <section id="why" className="relative overflow-hidden bg-legacy py-20 md:py-28 text-ivory">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 grid-lines opacity-[0.05]" />
          <div aria-hidden="true" className="pointer-events-none absolute top-[-6rem] left-1/2 -translate-x-1/2 h-96 w-[38rem] rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.16),transparent_70%)]" />
          <div className="container-lr relative">
            <div className="max-w-3xl">
              <div className="flex items-center gap-4"><span className="gold-rule" /><p className="eyebrow text-gold">Why Legacy Life &amp; Retirement Exists</p></div>
              <h2 className="mt-6 font-serif font-semibold tracking-display text-ivory text-3xl md:text-4xl lg:text-5xl leading-[1.06]">Education Should Come <span className="accent-word">Before Implementation.</span></h2>
              <p className="mt-7 text-taupe text-base leading-relaxed">Legacy Life &amp; Retirement exists to help families understand how financial tools, behaviors, and decisions may work together — before they are ever asked to implement a strategy.</p>
            </div>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((p, i) => (
                <div key={p.t} tabIndex={0} className="group rounded-card border border-gold/25 bg-white/[0.04] p-7 outline-none transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:bg-white/[0.08] focus-visible:-translate-y-1 focus-visible:border-gold">
                  <span className="font-serif text-3xl text-gold/80">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 font-serif text-lg md:text-xl text-ivory leading-tight">{p.t}</h3>
                  <p className="mt-3 text-taupe text-[0.88rem] leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 5. Credentials and Professional Role */}
        <Section id="credentials" className="bg-ivory">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow>Credentials and Role</Eyebrow>
              <Heading>A Verified Practitioner You Can <span className="accent-word">Trust.</span></Heading>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {roles.map(({ icon: Icon, t }) => (
                  <div key={t} className="lr-card p-5 flex items-start gap-3">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-legacy text-gold"><Icon size={18} strokeWidth={1.5} /></span>
                    <p className="text-legacy/90 text-[0.9rem] leading-relaxed pt-1.5">{t}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lr-card overflow-hidden bg-white">
              <img src={credential} alt="Authorized Infinite Banking Practitioner credential" loading="lazy" className="w-full object-contain bg-white p-6" />
              <div className="p-7 border-t border-taupe/40">
                <h3 className="font-serif text-xl text-legacy">Authorized Infinite Banking Practitioner</h3>
                <p className="mt-2 text-slate text-[0.9rem] leading-relaxed">Shawn’s practitioner status can be independently verified through the Infinite Banking Concepts directory.</p>
                <a href={PRACTITIONER_URL} target="_blank" rel="noopener noreferrer" data-testid="practitioner-verify-link" className="btn-base btn-outline-dark mt-5 text-sm"><ExternalLink size={16} />Verify Practitioner Status</a>
              </div>
            </div>
          </div>
        </Section>

        {/* 6. Working With Shawn */}
        <Section id="working-with" className="bg-[#EFE8D9]">
          <div className="max-w-2xl"><Eyebrow>Working With Shawn</Eyebrow><Heading>Clear Education. Honest Questions. <span className="accent-word">No Pressure.</span></Heading></div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {process.map((s) => (
              <div key={s.t} tabIndex={0} className="group lr-card p-8 outline-none transition-all duration-300 hover:-translate-y-1 hover:border-gold focus-visible:-translate-y-1 focus-visible:border-gold">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-legacy text-gold"><Phone size={18} strokeWidth={1.5} /></span>
                <p className="eyebrow mt-6 text-bronze">{s.tag}</p>
                <h3 className="mt-2 font-serif text-xl text-legacy leading-tight">{s.t}</h3>
                <p className="mt-3 text-slate text-[0.93rem] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <Link to="/strategy-call" data-testid="about-process-cta" className="btn-base btn-primary mt-10"><CalendarCheck size={18} />Learn About the Strategy Call</Link>
        </Section>

        {/* 7. Client Experiences */}
        <Section id="experiences" className="bg-ivory">
          <div className="max-w-2xl"><Eyebrow>Client Experiences</Eyebrow><Heading>In their <span className="accent-word">own words.</span></Heading></div>
          <div className="mt-12 grid md:grid-cols-3 gap-6 items-start">
            {primary.map((t, i) => <TestimonialCard key={t.id} t={t} index={i} />)}
          </div>
          <div className="mt-12 flex justify-center">
            <button type="button" onClick={() => setMore(true)} data-testid="about-view-more-testimonials" className="btn-base btn-outline-dark"><Users size={18} />View More Client Experiences</button>
          </div>
          <Modal open={more} onClose={() => setMore(false)} title="More Client Experiences" maxWidth="max-w-5xl">
            <div className="p-6 sm:p-9 clear-both">
              <p className="eyebrow text-bronze">Client Experiences</p>
              <h3 className="mt-2 font-serif text-2xl md:text-3xl text-legacy leading-tight">More stories from the Legacy Life community.</h3>
              <div className="mt-8 grid sm:grid-cols-2 gap-6 items-start">
                {extra.map((t) => <TestimonialCard key={t.id} t={t} surface="ivory" />)}
              </div>
            </div>
          </Modal>
        </Section>

        {/* 7b. Featured by the Nelson Nash Institute */}
        <Section id="nni-feature" className="bg-[#EFE8D9]">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow>Featured by the Nelson Nash Institute</Eyebrow>
              <Heading>A Conversation About Taking Control of Your <span className="accent-word">Financial Future.</span></Heading>
              <p className="mt-7 text-slate text-base leading-relaxed">
                In this episode of the Infinite Banking Initiative, Shawn shares how he discovered the Infinite Banking Concept, why he moved from IUL to properly structured whole life insurance, and how managing cash flow, using policy loans, and thinking long-term can create greater financial flexibility and control.
              </p>
              <p className="mt-6 flex items-start gap-2.5">
                <Play size={18} className="mt-1 shrink-0 text-gold" aria-hidden="true" />
                <span className="font-serif text-lg text-legacy leading-snug">How to Start IBC at Any Age and Take Control of Your Finances</span>
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a href={NNI_VIDEO_WATCH} target="_blank" rel="noopener noreferrer" data-testid="nni-watch-youtube" className="btn-base btn-primary"><Youtube size={18} />Watch on YouTube</a>
                <a href={PRACTITIONER_URL} target="_blank" rel="noopener noreferrer" data-testid="nni-practitioner-profile" className="btn-base btn-outline-dark"><ExternalLink size={16} />View Shawn&rsquo;s Authorized IBC Practitioner Profile</a>
              </div>
              <a href={NNI_EPISODE_URL} target="_blank" rel="noopener noreferrer" data-testid="nni-episode-link" className="mt-5 inline-flex items-center gap-1.5 text-sm text-bronze underline-offset-4 transition-colors hover:text-gold hover:underline focus-ring rounded-sm">
                View Episode 57 on the Nelson Nash Institute<ArrowRight size={15} />
              </a>
            </div>
            <div className="relative">
              <div aria-hidden="true" className="absolute -inset-2 rounded-card border border-gold/40 -z-10" />
              <div className="overflow-hidden rounded-card border border-taupe/50 bg-black shadow-[0_30px_60px_-35px_rgba(17,17,17,0.55)]">
                <div className="aspect-video w-full">
                  <iframe
                    className="h-full w-full"
                    src="https://www.youtube-nocookie.com/embed/iVnoFZpVcqg"
                    title="How to Start IBC at Any Age and Take Control of Your Finances"
                    loading="lazy"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    data-testid="nni-video-iframe"
                  />
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* 8. Final CTA */}
        <Section id="cta" className="bg-[#EFE8D9]">
          <div className="relative rounded-card bg-legacy overflow-hidden px-8 py-16 md:px-16 md:py-20 text-center">
            <div className="pointer-events-none absolute -top-px left-1/2 -translate-x-1/2 h-px w-40 bg-gold" />
            <div className="relative max-w-2xl mx-auto">
              <p className="eyebrow text-gold">Start With Education</p>
              <Heading light>You Do Not Need Everything Figured Out Before the <span className="accent-word">First Conversation.</span></Heading>
              <p className="mt-5 text-taupe text-base md:text-lg leading-relaxed">Bring your questions, goals, concerns, and current understanding. The purpose of the first call is to help clarify where you are and determine whether a deeper educational conversation makes sense.</p>
              <Link to="/strategy-call" data-testid="about-final-cta" className="btn-base btn-gold mt-9"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
            </div>
          </div>
        </Section>
      </main>
      <Footer />
      <StickyCta />
    </div>
  );
}
